import {TextEditorServices, TextUnderCursor} from '@frontapp/editor-html';
import {compact} from 'lodash';
import React, {FC, useEffect, useMemo, useState} from 'react';

import {
  KnowledgeBaseComposerDropdownInsertTrackingData,
  KnowledgeBaseInsertHandler,
} from '../../../../../../libs/knowledge-base/shared/components/composerIntegration/knowledgeBaseComposerDropdown';
import {isElement} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasPublishedExternalKnowledgeBase} from '../../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {getHasQuickInsertTable} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  COMMAND_START_TOKEN,
  computeInput,
  createTextIsBlankAt,
} from '../../../../../../libs/shared/ui/src/components/dropdowns/quickInsert/quickInsertCommon';
import {BoundsRect} from '../../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {isElementSelectionService} from '../../../../../helpers/editorHelpers';
import {AnswerModel} from '../../../../../models/api/answerModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {SchedulingLinkModel} from '../../../../../models/api/schedulingLinkModel';
import {VariableInterpolator} from '../../../../../services/variableInterpolation/variableInterpolator';
import {AnswerDialogContainerProps} from '../../../../inboxes/composer/answers/answerDialogContainer';
import {ServiceSubscription} from '../../serviceSubscription';
import {KnowledgeBaseQuickInsertDropdown} from './knowledgeBaseQuickInsertDropdown';
import {QuickInsertAnswersDropdown} from './quickInsertAnswersDropdown';
import {QuickInsertCommandDropdown} from './quickInsertCommandDropdown';
import {QuickInsertSchedulingDropdown} from './quickInsertSchedulingDropdown';
import {InsertTableDefinition, QuickInsertTableDropdown} from './quickInsertTableDropdown';
import {
  QuickInsertCommandsEnum,
  QuickInsertCommandSpec,
  QuickInsertDropdownsEnum,
  QuickInsertSelectedCommand,
} from './quickInsertTypes';

/*
 * Types.
 */

type QuickInsertDropdownSpec =
  | {
      type: QuickInsertDropdownsEnum.CHOOSE_COMMAND;
      anchor: BoundsRect;
    }
  | {
      type: QuickInsertDropdownsEnum.TEMPLATE;
      anchor: BoundsRect;
    }
  | {
      type: QuickInsertDropdownsEnum.SCHEDULING_LINK;
      anchor: BoundsRect;
    }
  | {
      type: QuickInsertDropdownsEnum.KNOWLEDGE_BASE;
      anchor: BoundsRect;
    }
  | {
      type: QuickInsertDropdownsEnum.TABLE;
      anchor: BoundsRect;
    };

/*
 * Constants.
 */

const TemplateCommand: QuickInsertCommandSpec = {
  type: QuickInsertCommandsEnum.TEMPLATE,
  name: '/template',
  description: <Trans id="quick-insert-command-description-template">Insert a message template</Trans>,
  iconName: 'messageTemplate',
  iconLabel: <Trans id="quick-insert-command-icon-label-template">Message templates</Trans>,
};

const SchedulingLinkCommand: QuickInsertCommandSpec = {
  type: QuickInsertCommandsEnum.SCHEDULING_LINK,
  name: '/schedule',
  description: <Trans id="quick-insert-command-description-scheduling-link">Insert a scheduling link</Trans>,
  iconName: 'schedule',
  iconLabel: <Trans id="quick-insert-command-icon-scheduling-link">Scheduling</Trans>,
};

const KnowledgeBaseCommand: QuickInsertCommandSpec = {
  type: QuickInsertCommandsEnum.KNOWLEDGE_BASE,
  name: '/article',
  description: (
    <Trans id="quick-insert-command-description-knowledge-base-article">
      Insert a knowledge base article link
    </Trans>
  ),
  iconName: 'knowledgeBase',
  iconLabel: <Trans id="quick-insert-command-icon-knowledge-base-article">Knowledge base</Trans>,
};

const TableCommand: QuickInsertCommandSpec = {
  type: QuickInsertCommandsEnum.TABLE,
  name: '/table',
  description: <Trans id="quick-insert-command-description-table">Insert a table</Trans>,
  iconName: 'tableGridSmall',
  iconLabel: <Trans id="quick-insert-command-icon-table">Table</Trans>,
};

/*
 * Props.
 */

export interface QuickInsertContainerProps extends AnswerDialogContainerProps {
  panelId: ComposerPanelId | ViewerPanelId;
  channelId: number;
  interpolator: VariableInterpolator;
  editorServices: TextEditorServices;
  editorWrapperRef: React.RefObject<HTMLDivElement>;
  conversation: ConversationModel | null;
  companyUrl: string;
  onAnswerSelect: (model: AnswerModel) => void;
  onSchedulingLinkInsertLink: (model: SchedulingLinkModel) => void;
  onKnowledgeBaseInsertContent: KnowledgeBaseInsertHandler;
  onTableInsert?: (definition: InsertTableDefinition) => void;
}

/*
 * Component.
 */

export const QuickInsertContainer: FC<QuickInsertContainerProps> = (props) => (
  <ServiceSubscription source={props.editorServices.textUnderCursor}>
    {({textUnderCursor}) => (
      <QuickInsertContainerComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        textUnderCursor={textUnderCursor}
      />
    )}
  </ServiceSubscription>
);

const QuickInsertContainerComponent = (
  props: QuickInsertContainerProps & {textUnderCursor: TextUnderCursor},
) => {
  const {editorWrapperRef, editorServices, textUnderCursor, panelId} = props;
  const autocompleteService = editorServices.autocomplete;

  const [dropdown, setDropdown] = useState<QuickInsertDropdownSpec | null>(null);
  const hasPublishedExternalKnowledgeBase = useAppSelector(getHasPublishedExternalKnowledgeBase);
  const hasQuickInsertTable = useAppSelector(getHasQuickInsertTable);

  const onClose = () => setDropdown(null);

  useEffect(() => {
    const wrapperElement = editorWrapperRef.current;

    if (dropdown || !wrapperElement) {
      return undefined;
    }

    const onKeyDownMaybeOpenQuickInsertDropdown = (event: KeyboardEvent) => {
      const {selection: selectionService, textUnderCursor: textUnderCursorService} = props.editorServices;

      if (!isElementSelectionService(selectionService)) {
        return;
      }

      const {selection} = selectionService;
      if (!selection) {
        return;
      }

      const {range} = selection;
      const {text, anchorOffset} = textUnderCursorService.textUnderCursor;

      const textIsBlankAt = createTextIsBlankAt(text);

      const shouldOpenCommandDropdown =
        range.collapsed &&
        event.key === COMMAND_START_TOKEN &&
        textIsBlankAt(anchorOffset) &&
        textIsBlankAt(anchorOffset - 1);

      if (!shouldOpenCommandDropdown) {
        return;
      }

      // If the selection is inside a DIV with no text content, the range's
      // start container will be the DIV (vs a text node). Querying such a range
      // for its bounding rect will return a rect that is "empty" (i.e.  all its
      // values are 0). So, in such cases, query the range's start container,
      // which will produce a non-empty rect.
      const anchor = isElement(range.startContainer)
        ? range.startContainer.getBoundingClientRect()
        : range.getBoundingClientRect();

      trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_OPEN);

      setDropdown({
        type: QuickInsertDropdownsEnum.CHOOSE_COMMAND,
        anchor,
      });
    };

    wrapperElement.addEventListener('keydown', onKeyDownMaybeOpenQuickInsertDropdown);

    return () => {
      wrapperElement.removeEventListener('keydown', onKeyDownMaybeOpenQuickInsertDropdown);
    };
  }, [dropdown, editorWrapperRef, props.editorServices]);

  const commands = useMemo(
    () =>
      compact([
        TemplateCommand,
        hasPublishedExternalKnowledgeBase && KnowledgeBaseCommand,
        SchedulingLinkCommand,
        hasQuickInsertTable && props.onTableInsert && TableCommand,
      ]),
    [hasPublishedExternalKnowledgeBase, hasQuickInsertTable, props.onTableInsert],
  );

  if (!dropdown) {
    return null;
  }

  const onBack = () => {
    trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_BACK, {
      dropdown: type,
    });
    setDropdown({
      type: QuickInsertDropdownsEnum.CHOOSE_COMMAND,
      anchor: dropdown.anchor,
    });
    editorFocus();
  };

  const editorFocus = () => props.editorServices.focus.focus();

  const {type, anchor} = dropdown;
  const anchorProvider = () => anchor;

  const clearInput = () => {
    const input = computeInput(textUnderCursor);
    autocompleteService.autocomplete(COMMAND_START_TOKEN + input, '');
  };

  switch (type) {
    case QuickInsertDropdownsEnum.CHOOSE_COMMAND: {
      const input = computeInput(textUnderCursor);

      const onCancel = () => {
        trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_CANCEL, {
          dropdown: QuickInsertDropdownsEnum.CHOOSE_COMMAND,
        });

        onClose();
      };

      const onSelect = (selected: QuickInsertSelectedCommand) => {
        const {command} = selected;

        setDropdown({
          type: getCommandDropdown(command),
          anchor,
        });
      };

      return (
        <QuickInsertCommandDropdown
          input={input}
          commands={commands}
          editorWrapperRef={props.editorWrapperRef}
          anchor={anchorProvider}
          onCancel={onCancel}
          onSelect={onSelect}
        />
      );
    }
    case QuickInsertDropdownsEnum.SCHEDULING_LINK: {
      const onCancel = () => {
        trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_CANCEL, {
          dropdown: QuickInsertDropdownsEnum.SCHEDULING_LINK,
        });
        onClose();
        editorFocus();
      };

      const onInsertLink = (link: SchedulingLinkModel) => {
        clearInput();
        props.onSchedulingLinkInsertLink(link);
        onClose();
      };

      return (
        <QuickInsertSchedulingDropdown
          panelId={props.panelId}
          anchor={anchorProvider}
          onBack={onBack}
          onCancel={onCancel}
          onSchedulingLinkInsertLink={onInsertLink}
        />
      );
    }
    case QuickInsertDropdownsEnum.TEMPLATE: {
      const onSelect = (answer: AnswerModel) => {
        clearInput();
        // Selection is tracked in `AnswersDropdown`.
        props.onAnswerSelect(answer);
        onClose();
      };

      const onCancel = () => {
        trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_CANCEL, {
          dropdown: QuickInsertDropdownsEnum.TEMPLATE,
        });
        onClose();
        editorFocus();
      };

      return (
        <QuickInsertAnswersDropdown
          namespace={props.namespace}
          conversation={props.conversation}
          channelId={props.channelId}
          message={props.message}
          editorServices={props.editorServices}
          interpolator={props.interpolator}
          anchor={anchorProvider}
          onSelect={onSelect}
          onCancel={onCancel}
          onBack={onBack}
        />
      );
    }

    case QuickInsertDropdownsEnum.KNOWLEDGE_BASE: {
      const onKnowledgeBaseInsertContent: KnowledgeBaseInsertHandler = (options) => {
        clearInput();
        props.onKnowledgeBaseInsertContent(options);
        onClose();
      };

      const onCancel = () => {
        trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_CANCEL, {
          dropdown: QuickInsertDropdownsEnum.KNOWLEDGE_BASE,
        });
        onClose();
        editorFocus();
      };

      const trackOnInsert = (data: KnowledgeBaseComposerDropdownInsertTrackingData) =>
        trackInterfaceEvent(InterfaceEventsEnum.KNOWLEDGE_BASE_ARTICLE_INSERT, data);

      return (
        <KnowledgeBaseQuickInsertDropdown
          anchor={anchorProvider}
          onKnowledgeBaseInsertContent={onKnowledgeBaseInsertContent}
          onCancel={onCancel}
          onBack={onBack}
          panelId={panelId}
          trackOnInsert={trackOnInsert}
        />
      );
    }

    case QuickInsertDropdownsEnum.TABLE: {
      const onCancel = () => {
        trackInterfaceEvent(InterfaceEventsEnum.QUICK_INSERT_CANCEL, {
          dropdown: QuickInsertDropdownsEnum.TABLE,
        });
        onClose();
        editorFocus();
      };

      const onSelect = (tableDefinition: InsertTableDefinition) => {
        clearInput();
        props.onTableInsert?.(tableDefinition);
        onClose();
      };

      return <QuickInsertTableDropdown anchor={anchorProvider} onCancel={onCancel} onSelect={onSelect} />;
    }
    default:
      throw new UnreachableError(type);
  }
};

function getCommandDropdown(command: QuickInsertCommandsEnum): QuickInsertDropdownsEnum {
  switch (command) {
    case QuickInsertCommandsEnum.TEMPLATE:
      return QuickInsertDropdownsEnum.TEMPLATE;

    case QuickInsertCommandsEnum.SCHEDULING_LINK:
      return QuickInsertDropdownsEnum.SCHEDULING_LINK;

    case QuickInsertCommandsEnum.KNOWLEDGE_BASE:
      return QuickInsertDropdownsEnum.KNOWLEDGE_BASE;

    case QuickInsertCommandsEnum.TABLE:
      return QuickInsertDropdownsEnum.TABLE;

    default:
      throw new UnreachableError(command);
  }
}
